import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ParidadService } from './services/paridad.service';
import { IParidad, IParidadSaveRequest } from './interfaces/paridad.interface';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-paridad-form',
  templateUrl: './paridad-form.component.html',
  providers: [MessageService]
})
export class ParidadFormComponent implements OnInit, OnChanges {
  @Input() paridad: IParidad | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();

  form!: FormGroup;
  loading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private paridadService: ParidadService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.resetForm();
    if (this.paridad) {
      this.loadParidad();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paridad']) {
      this.resetForm();
      if (changes['paridad'].currentValue) {
        this.loadParidad();
      }
    }
  }

  resetForm(): void {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = String(today.getFullYear()).slice(-2); // Solo dos dígitos
    const formattedDate = `${day}/${month}/${year}`;

    this.form = this.fb.group({
      fecha: [{ value: formattedDate, disabled: true }, [Validators.required]],
      descripcion: ['', [Validators.required]],
      paridaddolar: [0, [Validators.required, Validators.min(0)]],
      paridadudis: [0, [Validators.required, Validators.min(0)]],
      par2ODBC: [0]
    });
  }

  loadParidad(): void {
    if (this.paridad && this.paridad.par2ODBC) {
      this.loading = true;
      this.paridadService.getParidadForEdit(this.paridad.par2ODBC).subscribe({
        next: (response) => {
          if (response.success && response.data.length > 0) {
            const paridad = response.data[0];
            this.form.patchValue({
              fecha: paridad.fecha,
              descripcion: paridad.descripcion,
              paridaddolar: paridad.paridadDolar,
              paridadudis: paridad.paridadUdis,
              par2ODBC: paridad.par2ODBC
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo cargar la paridad'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar la paridad'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else if (this.paridad) {
      this.form.patchValue({
        fecha: this.paridad.fecha,
        descripcion: this.paridad.descripcion,
        paridaddolar: this.paridad.paridadDolar,
        paridadudis: this.paridad.paridadUdis,
        par2ODBC: this.paridad.par2ODBC
      });
    }
  }

  create(): void {
    if (this.form.valid) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      const payload: IParidadSaveRequest = {
        que: 1,
        tope: '',
        cmd: '',
        fecha: formValue.fecha,
        descripcion: formValue.descripcion,
        paridaddolar: formValue.paridaddolar,
        paridadudis: formValue.paridadudis,
        par2ODBC: formValue.par2ODBC
      };

      this.paridadService.saveParidad(payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({
              success: true,
              message: 'Paridad creada correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.data?.error || 'Error al crear la paridad'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear la paridad'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  update(): void {
    if (this.form.valid && this.paridad) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      const payload: IParidadSaveRequest = {
        que: 2,
        tope: '',
        cmd: '',
        fecha: formValue.fecha,
        descripcion: formValue.descripcion,
        paridaddolar: formValue.paridaddolar,
        paridadudis: formValue.paridadudis,
        par2ODBC: formValue.par2ODBC
      };

      this.paridadService.updateParidad(payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({
              success: true,
              message: 'Paridad actualizada correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.data?.error || 'Error al actualizar la paridad'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar la paridad'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'paridades',
        sClave: this.paridad?.par2ODBC.toString() || '',
        sNombre: this.form.get('descripcion')?.value,
        sTipo: 'Opcion'
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  clearForm(): void {
    this.resetForm();
  }
} 